/**
 * Edge Offset Utility
 * Detects parallel, bidirectional, fan-out, and fan-in edges and assigns offsets for visual separation
 *
 * Handles:
 * 1. Bidirectional edges (A→B and B→A)
 * 2. Fan-out edges (A→B, A→C, A→D from same source)
 * 3. Fan-in edges (B→A, C→A, D→A to same target)
 * 4. Parallel edges (multiple A→B with different labels)
 */

import type { DiagramEdge } from '../types/schema'

/**
 * Node position information for distance-aware offset calculation
 */
export interface NodePosition {
  id: string
  x: number
  y: number
}

/**
 * Configuration for edge offset calculation
 */
export interface EdgeOffsetConfig {
  /** Base offset distance in pixels (default: 28, tuned to stay inside containers) */
  baseOffset?: number
  /** Enable fan-out detection (edges from same source) */
  enableFanOut?: boolean
  /** Enable fan-in detection (edges to same target) */
  enableFanIn?: boolean
  /** Enable bidirectional detection (A→B and B→A) */
  enableBidirectional?: boolean
  /** Strategy for offset assignment */
  strategy?: 'symmetric' | 'staggered'
  /** Enable distance-aware offset scaling (scales offset based on edge length) */
  enableDistanceScaling?: boolean
  /** Node positions for distance-aware calculation (optional) */
  nodePositions?: NodePosition[]
}

/**
 * Edge group information for parallel/bidirectional detection
 */
interface EdgeGroup {
  edges: DiagramEdge[]
  isBidirectional: boolean
  nodeSet: string
}

/**
 * Fan-out group (multiple edges from same source + handle)
 */
interface FanOutGroup {
  sourceNode: string
  sourceHandle?: string  // NEW: Track which handle edges originate from
  edges: DiagramEdge[]
}

/**
 * Fan-in group (multiple edges to same target + handle)
 */
interface FanInGroup {
  targetNode: string
  targetHandle?: string  // NEW: Track which handle edges arrive at
  edges: DiagramEdge[]
}

const VERTICAL_ORIENTATIONS = new Set(['top', 'bottom'])
const HORIZONTAL_ORIENTATIONS = new Set(['left', 'right'])

function getOrientationAxis(edge: DiagramEdge): 'vertical' | 'horizontal' | 'mixed' | 'none' {
  const orientations: string[] = []

  const sourceOrientation = edge.data?.sourceOrientation
  if (typeof sourceOrientation === 'string' && sourceOrientation !== 'auto') {
    orientations.push(sourceOrientation)
  }

  const targetOrientation = edge.data?.targetOrientation
  if (typeof targetOrientation === 'string' && targetOrientation !== 'auto') {
    orientations.push(targetOrientation)
  }

  const hasVertical = orientations.some((value) => VERTICAL_ORIENTATIONS.has(value))
  const hasHorizontal = orientations.some((value) => HORIZONTAL_ORIENTATIONS.has(value))

  if (hasVertical && hasHorizontal) {
    return 'mixed'
  }

  if (hasVertical) {
    return 'vertical'
  }

  if (hasHorizontal) {
    return 'horizontal'
  }

  const preference = edge.data?.handlePreference
  if (preference === 'vertical' || preference === 'horizontal') {
    return preference
  }

  return 'none'
}

function shouldLockOffset(edge: DiagramEdge): boolean {
  // Edges that were separated by distinct perimeter ports (see
  // handle-assignment.ts) no longer need a curve offset to avoid overlap — the
  // ports themselves provide the separation. Lock their offset to 0.
  if (edge.data?.portSeparated) {
    return true
  }
  const axis = getOrientationAxis(edge)
  if (axis === 'vertical' || axis === 'horizontal') {
    return true
  }
  return false
}

/**
 * Process edges and assign offsets for parallel/bidirectional/fan-out/fan-in connections
 * This is the main function that handles edge separation
 */
export function assignEdgeOffsets(
  edges: DiagramEdge[],
  config: EdgeOffsetConfig = {}
): DiagramEdge[] {
  const {
    baseOffset = 28,           // Softer default keeps curves inside layer bounds
    enableFanOut = true,        // Enable by default
    enableFanIn = true,         // TIER 1: Changed from false to true - catch more patterns
    enableBidirectional = true, // Enable by default
    strategy = 'symmetric',
    enableDistanceScaling = true, // TIER 1: Enable distance-aware scaling
    nodePositions = [],         // Optional node positions for distance calculation
  } = config

  // Create node position map for quick lookup
  const nodePositionMap = new Map<string, NodePosition>()
  nodePositions.forEach((node) => {
    nodePositionMap.set(node.id, node)
  })

  // Create a map to store offsets for each edge
  const offsetMap = new Map<string, number>()

  // Priority 1: Handle bidirectional edges (A↔B)
  if (enableBidirectional) {
    const bidirectionalGroups = groupEdgesByNodePair(edges)
    bidirectionalGroups.forEach((group) => {
      if (group.isBidirectional && group.edges.length >= 2) {
        const eligibleEdges = group.edges.filter((edge) => !shouldLockOffset(edge))

        eligibleEdges.forEach((edge, index) => {
          const edgeKey = getEdgeKey(edge)

          // TIER 1: Calculate distance-aware offset
          const edgeDistance = calculateEdgeDistance(edge, nodePositionMap)
          const scaledBaseOffset = scaleOffsetByDistance(baseOffset, edgeDistance, enableDistanceScaling)

          const offset = calculateBidirectionalOffset(index, eligibleEdges.length, scaledBaseOffset)
          const clampedOffset = clampOffsetForEdge(offset, edgeDistance)
          offsetMap.set(edgeKey, clampedOffset)
        })

        group.edges.forEach((edge) => {
          const edgeKey = getEdgeKey(edge)
          if (!offsetMap.has(edgeKey) && shouldLockOffset(edge)) {
            offsetMap.set(edgeKey, 0)
          }
        })
      }
    })
  }

  // Priority 2: Handle fan-out edges (A→B, A→C, A→D)
  if (enableFanOut) {
    const fanOutGroups = groupEdgesBySource(edges)
    fanOutGroups.forEach((group) => {
      if (group.edges.length >= 2) {
        const edgesNeedingOffset = group.edges.filter((edge) => {
          const edgeKey = getEdgeKey(edge)
          if (offsetMap.has(edgeKey)) {
            return false
          }
          if (shouldLockOffset(edge)) {
            offsetMap.set(edgeKey, 0)
            return false
          }
          return true
        })

        edgesNeedingOffset.forEach((edge, index) => {
          const edgeKey = getEdgeKey(edge)
          // TIER 1: Calculate distance-aware offset
          const edgeDistance = calculateEdgeDistance(edge, nodePositionMap)
          const scaledBaseOffset = scaleOffsetByDistance(baseOffset, edgeDistance, enableDistanceScaling)

          const offset = calculateFanOutOffset(index, edgesNeedingOffset.length, scaledBaseOffset, strategy)
          const clampedOffset = clampOffsetForEdge(offset, edgeDistance)
          offsetMap.set(edgeKey, clampedOffset)
        })
      }
    })
  }

  // Priority 3: Handle fan-in edges (B→A, C→A, D→A)
  if (enableFanIn) {
    const fanInGroups = groupEdgesByTarget(edges)
    fanInGroups.forEach((group) => {
      if (group.edges.length >= 2) {
        const edgesNeedingOffset = group.edges.filter((edge) => {
          const edgeKey = getEdgeKey(edge)
          if (offsetMap.has(edgeKey)) {
            return false
          }
          if (shouldLockOffset(edge)) {
            offsetMap.set(edgeKey, 0)
            return false
          }
          return true
        })

        edgesNeedingOffset.forEach((edge, index) => {
          const edgeKey = getEdgeKey(edge)
          // TIER 1: Calculate distance-aware offset
          const edgeDistance = calculateEdgeDistance(edge, nodePositionMap)
          const scaledBaseOffset = scaleOffsetByDistance(baseOffset, edgeDistance, enableDistanceScaling)

          const offset = calculateFanInOffset(index, edgesNeedingOffset.length, scaledBaseOffset, strategy)
          const clampedOffset = clampOffsetForEdge(offset, edgeDistance)
          offsetMap.set(edgeKey, clampedOffset)
        })
      }
    })
  }

  // Apply offsets to edges
  const processedEdges = edges.map((edge) => {
    const edgeKey = getEdgeKey(edge)
    const offset = offsetMap.get(edgeKey) ?? 0

    return {
      ...edge,
      data: {
        ...edge.data,
        offset,
        edgeDirection: determineEdgeDirectionSimple(edge, edges),
      },
    }
  })

  return processedEdges
}

/**
 * Get unique key for an edge (HANDLE-AWARE)
 *
 * Includes sourceHandle and targetHandle to distinguish edges that connect
 * to different handles on the same nodes
 */
function getEdgeKey(edge: DiagramEdge): string {
  if (edge.id) return edge.id

  // Include handles in the key to distinguish edges using different connection points
  const sourceHandle = edge.sourceHandle || 'default'
  const targetHandle = edge.targetHandle || 'default'
  const label = edge.label || 'default'

  return `${edge.source}-${sourceHandle}-${edge.target}-${targetHandle}-${label}`
}

/**
 * Group edges by node pairs (bidirectional-aware)
 * Example: A→B and B→A are grouped together
 */
function groupEdgesByNodePair(edges: DiagramEdge[]): Map<string, EdgeGroup> {
  const groups = new Map<string, EdgeGroup>()

  edges.forEach((edge) => {
    const key = getNodePairKey(edge.source, edge.target)

    if (!groups.has(key)) {
      groups.set(key, {
        edges: [],
        isBidirectional: false,
        nodeSet: key,
      })
    }

    const group = groups.get(key)!
    group.edges.push(edge)
  })

  // Detect bidirectional connections
  groups.forEach((group) => {
    if (group.edges.length >= 2) {
      // Check if there are edges in both directions
      const [nodeA, nodeB] = group.nodeSet.split('<->')
      const forwardKey = `${nodeA}->${nodeB}`
      const backwardKey = `${nodeB}->${nodeA}`

      const hasForward = group.edges.some((e) =>
        getDirectionalKey(e.source, e.target) === forwardKey
      )
      const hasBackward = group.edges.some((e) =>
        getDirectionalKey(e.source, e.target) === backwardKey
      )

      group.isBidirectional = hasForward && hasBackward
    }
  })

  return groups
}

/**
 * Get node pair key (sorted for bidirectional grouping)
 * Example: A→B and B→A both return "A<->B"
 */
function getNodePairKey(source: string, target: string): string {
  const [node1, node2] = [source, target].sort()
  return `${node1}<->${node2}`
}

/**
 * Get directional key (preserves direction)
 * Example: A→B returns "A->B", B→A returns "B->A"
 */
function getDirectionalKey(source: string, target: string): string {
  return `${source}->${target}`
}

/**
 * Group edges by source node + handle (HANDLE-AWARE for fan-out detection)
 *
 * Edges from the SAME source node but DIFFERENT handles are grouped separately.
 * This prevents unnecessary offsets when edges are already physically separated
 * by their different source connection points.
 */
function groupEdgesBySource(edges: DiagramEdge[]): FanOutGroup[] {
  const groups = new Map<string, DiagramEdge[]>()

  edges.forEach((edge) => {
    // Create key that includes BOTH source node AND source handle
    const sourceHandle = edge.sourceHandle || 'default'
    const groupKey = `${edge.source}::${sourceHandle}`

    if (!groups.has(groupKey)) {
      groups.set(groupKey, [])
    }
    groups.get(groupKey)!.push(edge)
  })

  return Array.from(groups.entries()).map(([groupKey, edges]) => {
    const [sourceNode, sourceHandle] = groupKey.split('::')
    return {
      sourceNode,
      sourceHandle: sourceHandle === 'default' ? undefined : sourceHandle,
      edges,
    }
  })
}

/**
 * Group edges by target node + handle (HANDLE-AWARE for fan-in detection)
 *
 * Edges to the SAME target node but DIFFERENT handles are grouped separately.
 * This prevents unnecessary offsets when edges are already physically separated
 * by their different target connection points.
 */
function groupEdgesByTarget(edges: DiagramEdge[]): FanInGroup[] {
  const groups = new Map<string, DiagramEdge[]>()

  edges.forEach((edge) => {
    // Create key that includes BOTH target node AND target handle
    const targetHandle = edge.targetHandle || 'default'
    const groupKey = `${edge.target}::${targetHandle}`

    if (!groups.has(groupKey)) {
      groups.set(groupKey, [])
    }
    groups.get(groupKey)!.push(edge)
  })

  return Array.from(groups.entries()).map(([groupKey, edges]) => {
    const [targetNode, targetHandle] = groupKey.split('::')
    return {
      targetNode,
      targetHandle: targetHandle === 'default' ? undefined : targetHandle,
      edges,
    }
  })
}

/**
 * Calculate offset for bidirectional edges
 */
function calculateBidirectionalOffset(
  index: number,
  totalEdges: number,
  baseOffset: number
): number {
  if (totalEdges === 2) {
    // Simple bidirectional pair: +offset and -offset
    return index === 0 ? baseOffset : -baseOffset
  }

  // Multiple edges in bidirectional group
  const halfCount = (totalEdges - 1) / 2
  const offsetMultiplier = index - halfCount
  return Math.round(offsetMultiplier * baseOffset)
}

/**
 * Calculate offset for fan-out edges (multiple edges from same source)
 */
function calculateFanOutOffset(
  index: number,
  totalEdges: number,
  baseOffset: number,
  strategy: 'symmetric' | 'staggered'
): number {
  if (totalEdges === 1) {
    return 0
  }

  if (strategy === 'staggered') {
    // Staggered: 0, 20, 40, 60, 80
    return index * baseOffset
  }

  // Symmetric strategy (centered around 0)
  const halfCount = (totalEdges - 1) / 2
  const offsetMultiplier = index - halfCount

  // TIER 1: Use more aggressive scale factor for better separation
  const scaleFactor = totalEdges > 4 ? 2.0 : 1.2  // Increased from 1.5 and 1.0

  return Math.round(offsetMultiplier * baseOffset * scaleFactor)
}

/**
 * Calculate offset for fan-in edges (multiple edges to same target)
 */
function calculateFanInOffset(
  index: number,
  totalEdges: number,
  baseOffset: number,
  strategy: 'symmetric' | 'staggered'
): number {
  // Same logic as fan-out
  return calculateFanOutOffset(index, totalEdges, baseOffset, strategy)
}

/**
 * Determine edge direction (simplified version)
 */
function determineEdgeDirectionSimple(
  edge: DiagramEdge,
  allEdges: DiagramEdge[]
): 'forward' | 'backward' | 'single' {
  // Check if there's a reverse edge
  const reverseEdge = allEdges.find(
    (e) => e.source === edge.target && e.target === edge.source
  )

  if (!reverseEdge) {
    return 'single'
  }

  // Determine forward vs backward alphabetically
  const [node1, node2] = [edge.source, edge.target].sort()

  if (edge.source === node1 && edge.target === node2) {
    return 'forward'
  } else {
    return 'backward'
  }
}

/**
 * Detect if an edge is part of a bidirectional connection
 */
export function isBidirectionalEdge(
  edge: DiagramEdge,
  allEdges: DiagramEdge[]
): boolean {
  return allEdges.some(
    (other) =>
      other.source === edge.target &&
      other.target === edge.source
  )
}

/**
 * Get all parallel edges (edges between the same node pair)
 */
export function getParallelEdges(
  edge: DiagramEdge,
  allEdges: DiagramEdge[]
): DiagramEdge[] {
  const key = getNodePairKey(edge.source, edge.target)

  return allEdges.filter((other) => {
    const otherKey = getNodePairKey(other.source, other.target)
    return otherKey === key
  })
}

/**
 * TIER 1: Calculate the Euclidean distance between source and target nodes
 */
function calculateEdgeDistance(
  edge: DiagramEdge,
  nodePositionMap: Map<string, NodePosition>
): number {
  const sourceNode = nodePositionMap.get(edge.source)
  const targetNode = nodePositionMap.get(edge.target)

  // If positions not available, return default medium distance
  if (!sourceNode || !targetNode) {
    return 200 // Default distance
  }

  // Calculate Euclidean distance
  const dx = targetNode.x - sourceNode.x
  const dy = targetNode.y - sourceNode.y
  return Math.sqrt(dx * dx + dy * dy)
}

/**
 * TIER 1: Scale offset based on edge distance for better visual consistency
 *
 * Logic:
 * - Short edges (< 100px): Reduce offset to avoid excessive curvature
 * - Medium edges (100-300px): Use base offset as-is (optimal range)
 * - Long edges (> 300px): Increase offset for visibility
 *
 * Examples:
 * - 50px edge: offset 35 → 28 (80%)
 * - 150px edge: offset 35 → 35 (100%)
 * - 400px edge: offset 35 → 49 (140%)
 */
function scaleOffsetByDistance(
  baseOffset: number,
  distance: number,
  enableScaling: boolean
): number {
  if (!enableScaling || distance === 0) {
    return baseOffset
  }

  // Define scaling breakpoints
  const SHORT_EDGE = 100   // Below this: reduce offset
  const MEDIUM_EDGE = 300  // Above this: increase offset

  let scaleFactor = 1.0

  if (distance < SHORT_EDGE) {
    // Short edge: scale down (0.8x at 50px, 1.0x at 100px)
    scaleFactor = 0.8 + (distance / SHORT_EDGE) * 0.2
  } else if (distance > MEDIUM_EDGE) {
    // Long edge: scale up (1.0x at 300px, 1.4x at 500px+)
    const excessDistance = distance - MEDIUM_EDGE
    scaleFactor = 1.0 + Math.min(excessDistance / 500, 0.4)
  }

  return Math.round(baseOffset * scaleFactor)
}

function clampOffsetForEdge(offset: number, distance: number): number {
  if (!Number.isFinite(offset)) {
    return 0
  }

  const safeDistance = Number.isFinite(distance) ? Math.abs(distance) : NaN
  const maxByDistance = Number.isFinite(safeDistance) && safeDistance > 0
    ? Math.max(12, safeDistance * 0.35)
    : 120

  const maxAllowed = Math.min(120, maxByDistance)
  const clamped = Math.max(-maxAllowed, Math.min(maxAllowed, offset))

  return validateEdgeOffset(clamped)
}

/**
 * Validate that offsets are within reasonable bounds
 */
export function validateEdgeOffset(offset: number): number {
  const MAX_OFFSET = 120
  const MIN_OFFSET = -120

  return Math.max(MIN_OFFSET, Math.min(MAX_OFFSET, offset))
}
